import { httpsCallable } from 'firebase/functions';
import { functions } from './config';
import {
  AccountRecord,
  CreateAccountInput,
  UpdateAccountInput,
  CreateAccountResult,
} from '@/types/account';

/** callable のエラーか判定（サーバー側の HttpsError は日本語メッセージを持つ） */
const isCallableError = (error: unknown): error is Error => {
  if (!(error instanceof Error)) return false;
  const code = (error as Error & { code?: unknown }).code;
  return typeof code === 'string' && (code.startsWith('functions/') || code.startsWith('auth/'));
};

/**
 * アカウント管理サービスクラス
 * すべて Cloud Functions（callable）経由で操作する。
 * Admin SDK がルールをバイパスするため firestore.rules の改定は不要。
 */
export class AccountService {
  /** callable エラーは日本語メッセージをそのまま伝播し、それ以外は汎用メッセージに変換する */
  private toError(error: unknown, fallbackMessage: string): Error {
    if (isCallableError(error)) {
      return new Error(error.message);
    }
    return new Error(fallbackMessage);
  }

  /**
   * アカウント一覧を取得
   */
  async listAccounts(): Promise<AccountRecord[]> {
    try {
      const callable = httpsCallable<void, { accounts: AccountRecord[] }>(functions, 'listAccounts');
      const result = await callable();
      return result.data.accounts;
    } catch (error) {
      throw this.toError(error, 'アカウント一覧の取得に失敗しました');
    }
  }

  /**
   * アカウントを作成（初回パスワードは一度だけ返される。保存しないこと）
   */
  async createAccount(input: CreateAccountInput): Promise<CreateAccountResult> {
    try {
      const callable = httpsCallable<CreateAccountInput, CreateAccountResult>(
        functions,
        'createAccount'
      );
      const result = await callable(input);
      return result.data;
    } catch (error) {
      throw this.toError(error, 'アカウントの作成に失敗しました');
    }
  }

  /**
   * アカウントを更新
   */
  async updateAccount(input: UpdateAccountInput): Promise<AccountRecord> {
    try {
      const callable = httpsCallable<UpdateAccountInput, { account: AccountRecord }>(
        functions,
        'updateAccount'
      );
      const result = await callable(input);
      return result.data.account;
    } catch (error) {
      throw this.toError(error, 'アカウントの更新に失敗しました');
    }
  }

  /**
   * パスワードを再発行（新パスワードは一度だけ返される。保存しないこと）
   */
  async resetAccountPassword(uid: string): Promise<string> {
    try {
      const callable = httpsCallable<{ uid: string }, { newPassword: string }>(
        functions,
        'resetAccountPassword'
      );
      const result = await callable({ uid });
      return result.data.newPassword;
    } catch (error) {
      throw this.toError(error, 'パスワードの再発行に失敗しました');
    }
  }

  /**
   * アカウントを削除（自分自身は削除不可）
   */
  async deleteAccount(uid: string): Promise<void> {
    try {
      const callable = httpsCallable<{ uid: string }, { success: true }>(functions, 'deleteAccount');
      await callable({ uid });
    } catch (error) {
      throw this.toError(error, 'アカウントの削除に失敗しました');
    }
  }
}

// シングルトンインスタンスをエクスポート
export const accountService = new AccountService();
